import { useEffect, useRef, useState } from "react";
import { LuFilter, LuUser } from "react-icons/lu";
import { MdOutlineDelete } from "react-icons/md";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../../components/Button/Button";
import ConsultaFormContainer from "../../../components/ConsultaFormContainer/ConsultaFormContainer";
import Container from "../../../components/Container/Container";
import FilterApplied from "../../../components/FilterApplied/FilterApplied";
import Input from "../../../components/Input/Input";
import { LicenseUnitSelector } from "../../../components/LicenseUnitSelector/LicenseUnitSelector";
import ModalComponent from "../../../components/Modal/ModalComponent";
import Select from "../../../components/Select/Select";
import SearchContainer from "../../../components/SearchContainer/SearchContainer";
import SearchInput from "../../../components/SearchInput/SearchInput";
import SyncButton from "../../../components/SyncButton/SyncButton";
import { useListManager } from "../../../contexts/list.manager";
import { useUserStore } from "../../../contexts/userStore";
import { useAuth } from "../../../contexts/auth";
import TagService from "../../../services/TagService";
import FormCondoUsers from "../FormCondoUsers/FormCondoUsers";
import ListCondoUsers from "../ListCondoUsers/ListCondoUsers";
import "./ConsultaCondoUsers.css";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { useTranslation } from "react-i18next";

const EMPTY_FILTERS = {
	search: "",
	unitExternalId: "",
	carPlate: "",
	carModel: "",
	carBrand: "",
	profileTag: "",
	isTempEnabled: "",
	isTempExpired: "",
	tempStartDate: "",
	tempStartTime: "",
	tempEndDate: "",
	tempEndTime: "",
};

const FILTER_KEYS = Object.keys(EMPTY_FILTERS);

const FILTER_LABELS = {
	search: "Busca",
	unitExternalId: "Licença",
	carPlate: "Placa",
	carModel: "Modelo do veículo",
	carBrand: "Marca do veículo",
	profileTag: "Perfil",
	isTempEnabled: "Temporários",
	isTempExpired: "Expirados",
	tempStartDate: "Temporário a partir de",
	tempStartTime: "Horário inicial",
	tempEndDate: "Temporário até",
	tempEndTime: "Horário final",
};

function readFiltersFromUrl(searchParams) {
	const filters = FILTER_KEYS.reduce((filters, key) => {
		filters[key] = searchParams.get(key) ?? "";
		return filters;
	}, {});
	if (
		filters.isTempEnabled === "true" ||
		filters.isTempExpired === "true" ||
		filters.tempStartDate ||
		filters.tempEndDate
	) {
		filters.isTempEnabled = "true";
	}
	return filters;
}

const ConsultaCondoUsers = () => {
	const { lineSelected } = useListManager();
	const { selectedCondominio, condominiums } = useUserStore();
	const { user } = useAuth();
	const [searchParams, setSearchParams] = useSearchParams();
	const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
	const [draftFilters, setDraftFilters] = useState(() => readFiltersFromUrl(searchParams));
	const [showTemporaryFilters, setShowTemporaryFilters] = useState(
		() => readFiltersFromUrl(searchParams).isTempEnabled === "true",
	);
	const filtersRef = useRef(filters);
	filtersRef.current = filters;
	const lastSyncedCondominioRef = useRef(null);
	const [search, setSearch] = useState(() => searchParams.get("search") ?? "");
	const [profileTags, setProfileTags] = useState([]);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const [refreshKey, setRefreshKey] = useState(0);
	const serializedSearchParams = searchParams.toString();
	const { t } = useTranslation();

	useEffect(() => {
		const restoredFilters = readFiltersFromUrl(
			new URLSearchParams(serializedSearchParams),
		);
		setFilters(restoredFilters);
		setDraftFilters(restoredFilters);
		setSearch(restoredFilters.search);
		setShowTemporaryFilters(
			restoredFilters.isTempEnabled === "true",
		);
	}, [serializedSearchParams]);

	useEffect(() => {
		const selectedUnit = condominiums.find(
			(unit) => String(unit.id) === String(selectedCondominio),
		);
		const selectedCondominioKey = String(selectedCondominio);
		if (
			!selectedUnit?.externalId ||
			lastSyncedCondominioRef.current === selectedCondominioKey
		) {
			return;
		}
		lastSyncedCondominioRef.current = selectedCondominioKey;

		const currentFilters = filtersRef.current;
		if (currentFilters.unitExternalId === selectedUnit.externalId) {
			return;
		}

		const nextFilters = {
			...currentFilters,
			unitExternalId: selectedUnit.externalId,
		};
		setFilters(nextFilters);
		setDraftFilters(nextFilters);

		const nextSearchParams = new URLSearchParams();
		FILTER_KEYS.forEach((key) => {
			if (nextFilters[key]) {
				nextSearchParams.set(key, nextFilters[key]);
			}
		});
		setSearchParams(nextSearchParams, { replace: true });
	}, [selectedCondominio, condominiums, setSearchParams]);

	useEffect(() => {
		const fetchProfileTags = async () => {
			const response = await TagService.listTagsByClientAndType(
				user.userData.selectedOrganizationId,
				"PERSON",
			);
			if (response?.status === 200 && Array.isArray(response.data)) {
				setProfileTags(
					response.data
						.filter((tag) => tag.licensePerson)
						.sort((a, b) => a.name.localeCompare(b.name)),
				);
			}
		};

		fetchProfileTags();
	}, [user.userData.selectedOrganizationId]);

	useEffect(() => {
		if (location.state?.deletedId || location.state?.refresh) {
			setRefreshKey((k) => !k);
			navigate(location.pathname, { replace: true, state: {} });
		}
	}, [location.state, navigate, location.pathname]);

	const syncFiltersToUrl = (nextFilters) => {
		const nextSearchParams = new URLSearchParams();
		FILTER_KEYS.forEach((key) => {
			if (nextFilters[key]) {
				nextSearchParams.set(key, nextFilters[key]);
			}
		});
		setSearchParams(nextSearchParams, { replace: true });
	};

	const applyFilters = (nextFilters, syncSearchInput = true) => {
		setFilters(nextFilters);
		setDraftFilters(nextFilters);
		if (syncSearchInput) {
			setSearch(nextFilters.search);
		}
		syncFiltersToUrl(nextFilters);
	};

	const applySearch = () => {
		applyFilters({ ...filters, search });
	};

	const clearFilters = () => {
		const emptyFilters = { ...EMPTY_FILTERS };
		applyFilters(emptyFilters);
		setShowTemporaryFilters(false);
		setModalIsOpen(false);
	};

	const removeFilter = (key) => {
		const updatedFilters = { ...filters, [key]: "" };
		if (key === "isTempEnabled") {
			updatedFilters.isTempExpired = "";
			updatedFilters.tempStartDate = "";
			updatedFilters.tempStartTime = "";
			updatedFilters.tempEndDate = "";
			updatedFilters.tempEndTime = "";
			setShowTemporaryFilters(false);
		} else if (key === "isTempExpired") {
			updatedFilters.tempStartDate = "";
			updatedFilters.tempStartTime = "";
			updatedFilters.tempEndDate = "";
			updatedFilters.tempEndTime = "";
		}
		applyFilters(updatedFilters, key === "search");
	};

	const getFilterLabel = (key, value) => {
		if (["isTempEnabled", "isTempExpired"].includes(key)) {
			return value === "true" ? "Sim" : "Não";
		}
		if (key === "unitExternalId") {
			return condominiums.find((unit) => unit.externalId === value)?.name || value;
		}
		if (key === "profileTag") {
			return profileTags.find((tag) => tag.externalId === value)?.name || value;
		}
		return value;
	};

	return (
		<div className="consulta-container">
			<div className="search-list-wrapper">
				<SearchContainer
					title="Usuários"
					className="mb-1!"
					icon={<LuUser size={20} />}
					navigate="../cadastro-condo-users"
					buttonLabel="Adicionar Usuário"
					aditionalButton3={
						<SyncButton
							type={"condoUser"}
							selectedCondominio={selectedCondominio}
						/>
					}
					aditionalButton1={
						<Button
							className="button-white"
							label="Filtros"
							icon={<LuFilter size={18} />}
							onClick={() => {
								setDraftFilters(filters);
								setShowTemporaryFilters(filters.isTempEnabled === "true");
								setModalIsOpen(true);
							}}
						/>
					}
					aditionalButton2={
						<Button
							className="button-white"
							label="Limpar filtros"
							icon={<MdOutlineDelete size={17} />}
							onClick={clearFilters}
						/>
					}
				>
					<SearchInput
						value={search}
						placeholder="Buscar por nome"
						onChange={({ target }) => setSearch(target.value)}
						onKeyPress={(e) => e.key === "Enter" && applySearch()}
					/>
				</SearchContainer>
				<Container className="pt-0!">
					{FILTER_KEYS.some((key) => filters[key]) && (
						<div className="flex flex-wrap gap-2 mt-0!">
							{FILTER_KEYS.filter((key) => filters[key]).map((key) => (
								<FilterApplied
									key={key}
									filter={`${FILTER_LABELS[key]}: ${getFilterLabel(key, filters[key])}`}
									removeFilter={() => removeFilter(key)}
								/>
							))}
						</div>
					)}
				</Container>
				<Container className="mt-3!">
					<ListCondoUsers filters={filters} refreshKey={refreshKey} />
				</Container>
			</div>
			<ConsultaFormContainer>
				<FormCondoUsers condoUser={lineSelected} type="consulta" />
			</ConsultaFormContainer>
			<ModalComponent
				modalIsOpen={modalIsOpen}
				modalText="Filtros de usuários"
				handleConfirm={() => {
					applyFilters({ ...draftFilters, search: filters.search }, false);
					setModalIsOpen(false);
				}}
				handleCancel={() => setModalIsOpen(false)}
			>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-[min(80vw,700px)]">
					<LicenseUnitSelector
						label="Licença"
						unitId={
							condominiums.find(
								(unit) => unit.externalId === draftFilters.unitExternalId,
							)?.id
						}
						setUnitId={(id) => {
							const unit = condominiums.find(
								(item) => String(item.id) === String(id),
							);
							setDraftFilters((current) => ({
								...current,
								unitExternalId: unit?.externalId || "",
							}));
						}}
					/>
					<Select
						labelText="Perfil do usuário"
						value={draftFilters.profileTag}
						options={profileTags.map((tag) => ({
							value: tag.externalId,
							text: tag.name,
						}))}
						onChange={({ target }) =>
							setDraftFilters((current) => ({
								...current,
								profileTag: target.value,
							}))
						}
					/>
					<div className="md:col-span-2">
						<ToggleButton
							label="Temporários"
							selected={draftFilters.isTempEnabled === "true"}
							onChange={({ target }) => {
								setShowTemporaryFilters(target.checked);
								setDraftFilters((current) => ({
									...current,
									isTempEnabled: target.checked ? "true" : "",
									isTempExpired: "",
									...(!target.checked && {
										tempStartDate: "",
										tempStartTime: "",
										tempEndDate: "",
										tempEndTime: "",
									}),
								}));
							}}
						/>
					</div>
					{showTemporaryFilters && (
						<div className="md:col-span-2">
							<ToggleButton
								label="Expirados"
								selected={draftFilters.isTempExpired === "true"}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										isTempExpired: target.checked ? "true" : "",
										...(target.checked && {
											tempStartDate: "",
											tempStartTime: "",
											tempEndDate: "",
											tempEndTime: "",
										}),
									}))
								}
							/>
						</div>
					)}
					{showTemporaryFilters && draftFilters.isTempExpired !== "true" && (
						<>
							<div className="filters-group group-date-range mb-3.5! md:col-span-2">
								<h3 className="filters-group-title">{t("common.timeInterval")}</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full!">
									<Input
										type="date"
										labelText={t("common.startDate")}
										value={draftFilters.tempStartDate}
										onChange={({ target }) =>
											setDraftFilters((current) => ({
												...current,
												tempStartDate: target.value,
												tempStartTime: target.value ? current.tempStartTime : "",
											}))
										}
									/>
									<Input
										type="time"
										min="00:00"
										max="23:59"
										labelText={t("common.startTime")}
										value={draftFilters.tempStartTime}
										disabled={!draftFilters.tempStartDate}
										onChange={({ target }) =>
											setDraftFilters((current) => ({
												...current,
												tempStartTime: target.value,
											}))
										}
									/>
									<Input
										type="date"
										labelText={t("common.endDate")}
										value={draftFilters.tempEndDate}
										onChange={({ target }) =>
											setDraftFilters((current) => ({
												...current,
												tempEndDate: target.value,
												tempEndTime: target.value ? current.tempEndTime : "",
											}))
										}
									/>
									<Input
										type="time"
										min="00:00"
										max="23:59"
										labelText={t("common.endTime")}
										value={draftFilters.tempEndTime}
										disabled={!draftFilters.tempEndDate}
										onChange={({ target }) =>
											setDraftFilters((current) => ({
												...current,
												tempEndTime: target.value,
											}))
										}
									/>
								</div>
							</div>
						</>
					)}

					<div className="filters-group group-date-range mt-2! mb-3.5! md:col-span-2">
						<h3 className="filters-group-title">{t("common.vehicle")}</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full!">
							<Input
								labelText="Placa do veículo"
								value={draftFilters.carPlate}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										carPlate: target.value,
									}))
								}
							/>
							<Input
								labelText="Modelo do veículo"
								value={draftFilters.carModel}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										carModel: target.value,
									}))
								}
							/>
							<Input
								labelText="Marca do veículo"
								value={draftFilters.carBrand}
								onChange={({ target }) =>
									setDraftFilters((current) => ({
										...current,
										carBrand: target.value,
									}))
								}
							/>
						</div>
					</div>
				</div>
			</ModalComponent>
		</div>
	);
};

export default ConsultaCondoUsers;
